import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/layouts/AdminLayout';
import { getProducts } from '../../services/productService';
import { getCategories } from '../../services/categoryService';
import { getSubcategories } from '../../services/subcategoryService';
import { useAuth } from '../../context/AuthContext';
import { FiPackage, FiLayers, FiGrid, FiTag } from 'react-icons/fi';

const DashboardPage = () => {
    const [stats, setStats] = useState({
        products: 0,
        categories: 0,
        subcategories: 0,
        loading: true
    });

    const [recentProducts, setRecentProducts] = useState([]);
    const { user } = useAuth();

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch products with limit to get total count and recent products
                const productsRes = await getProducts({ limit: 5, sort: '-createdAt' });

                // Fetch categories and subcategories
                const categoriesRes = await getCategories();
                const subcategoriesRes = await getSubcategories();

                setStats({
                    products: productsRes.total || 0,
                    categories: categoriesRes.count || 0,
                    subcategories: subcategoriesRes.count || 0,
                    loading: false
                });

                setRecentProducts(productsRes.data || []);
            } catch (error) {
                console.error('Error fetching dashboard data:', error);
                setStats(prev => ({ ...prev, loading: false }));
            }
        };

        fetchData();
    }, []);

    const StatCard = ({ title, value, icon, color }) => (
        <div className="p-6 bg-[#f8f8f8] rounded-lg shadow-md">
            <div className="flex items-center">
                <div className={`p-3 rounded-full ${color}`}>
                    {icon}
                </div>
                <div className="mx-4">
                    <h4 className="text-2xl font-semibold text-gray-800">{value}</h4>
                    <div className="text-gray-500">{title}</div>
                </div>
            </div>
        </div>
    );

    return (
        <AdminLayout>
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-800">Dashboard</h1>
                <p className="mt-1 text-gray-600">Welcome back, {user?.name}!</p>
            </div>

            {stats.loading ? (
                <div className="flex justify-center py-10">
                    <div className="w-10 h-10 border-t-2 border-b-2 border-primary-600 rounded-full animate-spin"></div>
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-1 gap-5 mb-8 sm:grid-cols-2 lg:grid-cols-4">
                        <StatCard
                            title="Total Products"
                            value={stats.products}
                            icon={<FiPackage size={24} className="text-white" />}
                            color="bg-primary-600"
                        />
                        <StatCard
                            title="Categories"
                            value={stats.categories}
                            icon={<FiLayers size={24} className="text-white" />}
                            color="bg-primary-500"
                        />
                        <StatCard
                            title="Subcategories"
                            value={stats.subcategories}
                            icon={<FiGrid size={24} className="text-white" />}
                            color="bg-primary-400"
                        />
                        <StatCard
                            title="Your Role"
                            value={user?.role}
                            icon={<FiTag size={24} className="text-white" />}
                            color="bg-primary-300"
                        />
                    </div>

                    {/* Recent Products */}
                    <div className="mb-8">
                        <h2 className="mb-4 text-xl font-semibold text-gray-800">Recent Products</h2>
                        <div className="overflow-hidden bg-[#f8f8f8] rounded-lg shadow-md">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                                            Product
                                        </th>
                                        <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                                            Brand
                                        </th>
                                        <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                                            Category
                                        </th>
                                        <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                                            Price
                                        </th>
                                        <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                                            Rating
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-[#f8f8f8] divide-y divide-gray-200">
                                    {recentProducts.length > 0 ? (
                                        recentProducts.map((product) => (
                                            <tr key={product._id} className="hover:bg-gray-50">
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex items-center">
                                                        <div className="flex-shrink-0 w-10 h-10">
                                                            <img
                                                                className="object-cover w-10 h-10 rounded-md"
                                                                src={product.imageUrl || '/placeholder.png'}
                                                                alt={product.name}
                                                            />
                                                        </div>
                                                        <div className="ml-4">
                                                            <div className="text-sm font-medium text-gray-900">{product.name}</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm text-gray-900">{product.brand}</div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm text-gray-900">
                                                        {product.category?.name || 'N/A'}
                                                    </div>
                                                    <div className="text-xs text-gray-500">
                                                        {product.subcategory?.name || 'N/A'}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm text-gray-900">${product.price.toFixed(2)}</div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex items-center">
                                                        <span className="text-sm text-gray-900 mr-1">{product.rating}</span>
                                                        <span className="text-xs text-primary-600">★</span>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                                                No products found
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}
        </AdminLayout>
    );
};

export default DashboardPage;